import { computed, inject, Injectable, signal } from '@angular/core';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { DateOnly, LoaderService, Patient, PatientSettingsBase, TreatmentPlanSettings } from '../core';
import { PatientSettingsForm } from '../models';
import { PatientService } from './patient.service';
import { TreatmentService } from './treatment.service';
import { UserService } from './user.service';


/**
 * This service handles patient settings page data.
 */
@Injectable({ providedIn: 'root' })
export class PatientSettingsService {

	private readonly usrSrv = inject(UserService);
	private readonly patSrv = inject(PatientService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly treatSrv = inject(TreatmentService);

	patient = signal<Patient | null>(null);

	patSettings = signal<PatientSettingsForm | null>(null);
	isContactInfoValid = signal<boolean>(false);
	isPinSiteCareValid = signal<boolean>(false);
	isMotMessagesValid = signal<boolean>(false);
	publishedPlans = signal<TreatmentPlanSettings[] | null>(null);

	isReadonly = signal<boolean>(true);
	isPatientValid = computed(() => this.isContactInfoValid() && this.isPinSiteCareValid() && this.isMotMessagesValid());


	/**
	 * Init patient settings page data.
	 */
	init(patientId: string): Observable<boolean> {
		this.loaderSrv.show();
		return this.patSrv.getPatient(patientId).pipe(
			tap(pat => this.patient.set(pat)),
			switchMap(pat => forkJoin([
				this.patSrv.getPatSettings(pat.id, pat.userGuid),
				this.treatSrv.getPublishedPlansProgress(pat.id, pat.userGuid),
				this.usrSrv.isCustomerCareOtherUser(pat.userGuid)
			])),
			map(([patStgs, plans, isReadonly]) => {
				if (!patStgs) return false;
				this.patSettings.set(this.patSrv.patSettingsFormMapper(patStgs));
				this.publishedPlans.set(plans);
				this.isReadonly.set(isReadonly);
				return true;
			}),
			finalize(() => this.loaderSrv.hide())
		);
	}

	/**
	 * Update patient settings.
	 */
	update(): Observable<void> {
		this.loaderSrv.show();
		const settings = this._patientSettings;
		return this.patSrv.savePatSettings(this.patient()!.id, settings).pipe(
			catchError(err => {
				this.loaderSrv.hide();
				return throwError(() => err);
			})
		);
	}

	private get _patientSettings(): PatientSettingsBase {
		return {
			email: this.patSettings()!.email,
			secondEmail: this.patSettings()!.secondEmail,
			pinSiteCare: {
				enable: !!this.patSettings()!.pinSiteCareEnable,
				firstDay: this.patSettings()!.firstDay ? new DateOnly(this.patSettings()!.firstDay!) : undefined,
				frequency: this.patSettings()!.frequency!
			},
			motivationalMessage: {
				enable: !!this.patSettings()!.mmEnable,
				enabledSet: this.patSettings()!.enabledSet?.length ? this.patSettings()!.enabledSet : undefined,
				customSet: this.patSettings()!.customSet
			}
		};
	}

	/**
	 * Revoke all treatment plans.
	 */
	revokeAll(): Observable<void> {
		this.loaderSrv.show();
		return this.treatSrv.revokeAll(this.patient()!.id).pipe(
			catchError(err => {
				this.loaderSrv.hide();
				return throwError(() => err);
			})
		);
	}

	/**
	 * Update patient file.
	 */
	// updateFile(file: File, category: FileCategory): Observable<void> {
	// 	this.loaderSrv.show();
	// 	return this.patSrv.saveFile(this.patient()!.id, file, category).pipe(
	// 		finalize(() => this.loaderSrv.hide())
	// 	);
	// }

	/**
	 * Delete patient file.
	 */
	// deleteFile(category: FileCategory): Observable<void> {
	// 	this.loaderSrv.show();
	// 	return this.patSrv.deleteFile(this.patient()!.id, category).pipe(
	// 		finalize(() => this.loaderSrv.hide())
	// 	);
	// }

	/**
	 * Reset patient settings page data.
	 */
	reset(): void {
		this.patient.set(null);
		this.patSettings.set(null);
		this.isContactInfoValid.set(false);
		this.isPinSiteCareValid.set(false);
		this.isMotMessagesValid.set(false);
		this.publishedPlans.set(null);
	}
}